import type { CapitalReport } from './types';

export const G3_REPORTS: CapitalReport[] = [
  {
    key: 'G3R1', group: 'G3', rCode: 'R1', domain: 'Revenue Readiness',
    stage: 'Exploration Stage (Monetization Discovery Phase)', tone: 'red', profile: 'Market Explorer',
    intro: "This report evaluates your startup's ability to generate real, repeatable revenue from your product and users.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You are at the very beginning of your revenue journey.' },
        { kind: 'paragraph', text: 'At this stage, you may have: a product; users; early interest' },
        { kind: 'paragraph', text: 'However, you do not yet have validated revenue.' },
        { kind: 'paragraph', text: 'Key Reality: Interest is not revenue; Users are not customers' },
        { kind: 'paragraph', text: 'From an investor perspective, this means: high uncertainty; no monetization proof; no validated business model' },
        { kind: 'callout', text: 'You are now entering: The Monetization Discovery Phase' },
      ]},
      { title: 'Revenue Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Result'], rows: [
          [{ text: 'Revenue Stage' }, { text: 'Exploration', tone: 'red' }],
          [{ text: 'Revenue Status' }, { text: 'No consistent revenue', tone: 'red' }],
          [{ text: 'Business Model' }, { text: 'Undefined / untested', tone: 'orange' }],
          [{ text: 'GTM' }, { text: 'Not structured', tone: 'red' }],
          [{ text: 'Risk Level' }, { text: 'Very High', tone: 'red' }],
          [{ text: 'Probability of Success' }, { text: 'Low (10–25%) under current conditions' }],
        ]},
      ]},
      { title: 'Revenue Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'What a Revenue-Generating Startup Requires vs Your Current Position' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required for Revenue'], rows: [
          [{ text: 'Revenue Model' }, { text: 'Not defined', tone: 'red' }, { text: 'Clear monetization strategy' }],
          [{ text: 'Pricing' }, { text: 'Not tested', tone: 'red' }, { text: 'Real willingness-to-pay validation' }],
          [{ text: 'Sales Process' }, { text: 'Ad hoc', tone: 'red' }, { text: 'Structured funnel' }],
          [{ text: 'Acquisition Channels' }, { text: 'Undefined', tone: 'red' }, { text: '1–2 focused channels' }],
          [{ text: 'Metrics' }, { text: 'Missing', tone: 'red' }, { text: 'CAC, LTV, conversion tracking' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Your current position is driven by:' },
        { kind: 'bullets', items: [
          'No meaningful or consistent revenue',
          'No structured go-to-market (GTM) strategy',
          'Pricing has not been tested with real users',
          'No tracking of conversion or monetization metrics',
        ]},
        { kind: 'callout', text: 'This creates a fundamental gap: "Product exists → Business does not yet exist"' },
      ]},
      { title: 'Critical Risks', blocks: [
        { kind: 'steps', items: [
          { title: 'False Product Validation', sub: ['Users engaging with your product does not mean they are willing to pay'] },
          { title: 'No Revenue Engine', sub: ['There is no repeatable system to generate income'] },
          { title: 'Investor Rejection Risk', sub: ['Without revenue or monetization signals: funding becomes extremely difficult'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 3–5 Weeks)', blocks: [
        { kind: 'steps', items: [
          { title: 'Define a Simple Revenue Model (CRITICAL)', sub: ['Choose ONE primary model: subscription; transaction; service. Focus: simplicity + speed of testing'] },
          { title: 'Launch First Monetization Test', sub: ['Ask users to pay; Even a small amount ($5–$50). Goal: Proof > Profit'] },
          { title: 'Run 2–3 GTM Experiments', sub: ['Test simple acquisition channels: LinkedIn outreach; niche communities; direct sales. Track: leads; conversions; feedback'] },
          { title: 'Track Basic Revenue Metrics', sub: ['Start immediately: CAC (Customer Acquisition Cost); conversion rate; early LTV estimation'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are not building a business yet. You are testing if a business can exist' },
        { kind: 'paragraph', text: 'Your mindset must shift to: Scientist Mode: hypothesis = product; experiment = revenue; result = validation' },
        { kind: 'callout', text: 'Your goal is to move from: $0 → $1 → $100 → $1,000' },
      ]},
      { title: 'Your Revenue Path', blocks: [
        { kind: 'path', steps: ['No Revenue', 'Exploration Stage', 'First Revenue', 'Repeatable Revenue', 'Scalable Revenue'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Revenue Validation Sprint' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'define your revenue model',
          'test pricing with real users',
          'run structured GTM experiments',
          'validate your first paying customers',
        ]},
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: [
          'Define your monetization model today',
          'Ask your first users to pay this week',
          'Launch 2 GTM experiments',
          'Start tracking conversions immediately',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Stop validating your idea. Validate your revenue', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G3R2', group: 'G3', rCode: 'R2', domain: 'Revenue Readiness',
    stage: 'First Revenue Stage (Initial Monetization Phase)', tone: 'yellow', profile: 'First Seller',
    intro: 'This report evaluates your ability to convert early revenue into a repeatable and scalable business model.',
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have crossed a critical milestone: You have generated your first revenue' },
        { kind: 'paragraph', text: 'This means: users are willing to pay; your idea has real market signal; your product is commercially viable (early)' },
        { kind: 'paragraph', text: 'However: First revenue does NOT mean: product-market fit; a scalable business; predictable income' },
        { kind: 'paragraph', text: 'Your revenue today is: inconsistent; experimental; not repeatable yet' },
        { kind: 'callout', text: 'You are now entering: The Initial Monetization Phase' },
      ]},
      { title: 'Revenue Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Result'], rows: [
          [{ text: 'Revenue Stage' }, { text: 'First Revenue', tone: 'yellow' }],
          [{ text: 'Revenue Status' }, { text: 'Early / inconsistent', tone: 'orange' }],
          [{ text: 'Business Model' }, { text: 'Partially validated', tone: 'orange' }],
          [{ text: 'GTM' }, { text: 'Informal', tone: 'orange' }],
          [{ text: 'Risk Level' }, { text: 'Medium–High', tone: 'yellow' }],
          [{ text: 'Probability of Success' }, { text: '25–45%' }],
        ]},
      ]},
      { title: 'Revenue Gap Analysis', blocks: [
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required'], rows: [
          [{ text: 'Revenue' }, { text: 'First payments', tone: 'yellow' }, { text: 'Consistent revenue flow' }],
          [{ text: 'Pricing' }, { text: 'Tested once', tone: 'orange' }, { text: 'Validated pricing model' }],
          [{ text: 'Sales' }, { text: 'Opportunistic', tone: 'orange' }, { text: 'Structured process' }],
          [{ text: 'Channels' }, { text: 'Unclear', tone: 'orange' }, { text: 'Repeatable acquisition channel' }],
          [{ text: 'Metrics' }, { text: 'Basic', tone: 'orange' }, { text: 'CAC, LTV, conversion tracking' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Based on your inputs:' },
        { kind: 'bullets', items: [
          'You have generated initial revenue',
          'Payments are not consistent or predictable',
          'Sales happen through effort, not system',
          'No structured acquisition channel',
        ]},
        { kind: 'callout', text: 'This creates: "Revenue exists → System does not yet exist"' },
      ]},
      { title: 'Critical Risks', blocks: [
        { kind: 'steps', items: [
          { title: 'False Confidence', sub: ['First sales can create illusion of success'] },
          { title: 'No Repeatability', sub: ["You don't yet know: how to consistently acquire customers; what drives conversions"] },
          { title: 'Weak Revenue Foundation', sub: ['Without structure: revenue will plateau; growth will stall'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 30–45 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Identify What Worked (CRITICAL)', sub: ['Analyze: where first customers came from; why they paid; what triggered conversion. Goal: find signal'] },
          { title: 'Define a Simple Sales Process', sub: ['Create: lead → conversation → conversion. Even basic: script; pitch; offer'] },
          { title: 'Focus on ONE Channel', sub: ['Choose your strongest: LinkedIn; direct outreach; referrals; communities. Goal: repeatability'] },
          { title: 'Validate Pricing Properly', sub: ['Test different price points; Observe conversion. Goal: pricing clarity'] },
          { title: 'Track Core Metrics', sub: ['Start tracking: conversion rate; CAC (rough); revenue per customer'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are no longer testing if people will pay. You are now testing: "Can this revenue be repeated?"' },
        { kind: 'paragraph', text: 'Your mindset: Operator Mode: structure; discipline; repeatability' },
        { kind: 'callout', text: 'Your goal: $1K → $5K → $10K (consistent)' },
      ]},
      { title: 'Your Path', blocks: [
        { kind: 'path', steps: ['Exploration', 'First Revenue', 'Repeatable Revenue', 'Scalable Revenue'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Revenue System Builder' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'structure your sales',
          'define your best channel',
          'validate pricing',
          'build repeatable revenue',
        ]},
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: [
          'Analyze first 5–10 customers',
          'Document your sales flow',
          'Focus on one acquisition channel',
          'Track conversions',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Turn your first revenue into a repeatable system', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G3R3', group: 'G3', rCode: 'R3', domain: 'Revenue Readiness',
    stage: 'Early Sales Stage (Stabilization Phase)', tone: 'yellow', profile: 'Early Seller',
    intro: 'This report evaluates your ability to turn early sales into a repeatable and structured revenue system.',
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have crossed an important milestone in your business journey: You are generating repeat sales' },
        { kind: 'paragraph', text: 'This means: customers are paying more than once; new customers are arriving through semi-consistent channels; early traction signals are visible' },
        { kind: 'paragraph', text: 'This is a strong validation signal.' },
        { kind: 'paragraph', text: 'However, your business is still fragile: revenue is not yet predictable; sales are not fully structured; growth is not scalable' },
        { kind: 'paragraph', text: 'From an investor perspective: There is traction, but it is not yet reliable' },
        { kind: 'callout', text: 'You are currently in: The Sales Stabilization Phase' },
      ]},
      { title: 'Revenue Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Revenue Stage' }, { text: 'Early Sales', tone: 'yellow' }],
          [{ text: 'Revenue Status' }, { text: 'Semi-consistent', tone: 'orange' }],
          [{ text: 'Sales Process' }, { text: 'Informal', tone: 'orange' }],
          [{ text: 'Pricing Model' }, { text: 'Semi-structured', tone: 'orange' }],
          [{ text: 'Acquisition Channels' }, { text: 'Multiple but unclear ROI', tone: 'orange' }],
          [{ text: 'Metrics Discipline' }, { text: 'Weak', tone: 'red' }],
          [{ text: 'Risk Level' }, { text: 'Medium–High', tone: 'yellow' }],
          [{ text: 'Probability of Success' }, { text: '40–60%' }],
        ]},
      ]},
      { title: 'Revenue Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Required for Repeatable Growth' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required to Progress'], rows: [
          [{ text: 'Revenue' }, { text: 'Exists & repeating', tone: 'green' }, { text: 'Must become predictable' }],
          [{ text: 'Sales Funnel' }, { text: 'Not fully defined', tone: 'red' }, { text: 'Clear end-to-end funnel' }],
          [{ text: 'Pricing' }, { text: 'Flexible', tone: 'orange' }, { text: 'Standardized tiers' }],
          [{ text: 'Channels' }, { text: 'Multiple tests', tone: 'orange' }, { text: '1–2 optimized channels' }],
          [{ text: 'Metrics' }, { text: 'Loosely tracked', tone: 'red' }, { text: 'CAC, LTV, churn discipline' }],
        ]},
        { kind: 'paragraph', text: 'You have traction, but not yet a system.' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Based on your diagnostic inputs:' },
        { kind: 'bullets', items: [
          'Customers are returning or new ones are converting',
          'Sales are happening through a semi-consistent process',
          'You are testing multiple channels (outbound, inbound, referrals)',
          'Pricing is set, but still flexible or negotiated case by case',
          'Metrics like CAC and LTV are tracked loosely',
        ]},
        { kind: 'callout', text: 'This creates: "Growth exists, but it is unstable"' },
      ]},
      { title: 'Critical Risks Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Fragile Growth Illusion', sub: ['Growth feels strong, but may still be: unpredictable; founder-dependent; hard to repeat'] },
          { title: 'No Defined Sales Engine', sub: ['You lack: structured funnel stages; consistent conversion process; repeatable sales logic'] },
          { title: 'Pricing Inconsistency', sub: ['Flexible pricing can create: customer confusion; weak margins; poor scalability'] },
          { title: 'Weak Metrics Discipline', sub: ['Without consistent tracking: optimization is difficult; scaling is risky; investor confidence drops'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 60–90 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Standardize Your Sales Process (CRITICAL)', sub: ['Document your full funnel: awareness → interest → conversion → retention. Identify what works; Eliminate what doesn\'t. Build playbooks for: outreach; demos; onboarding. Objective: Turn sales from effort into process'] },
          { title: 'Strengthen Retention & Monetization', sub: ['Improve onboarding experience; Track churn closely; Identify drop-off points; Test: upsells; cross-sells. Goal: Increase revenue per customer'] },
          { title: 'Build Metrics Discipline', sub: ['Start tracking consistently: CAC; LTV; gross margin; payback period; monthly revenue growth. Key principle: Consistency beats perfection'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are no longer testing whether customers will pay. You already know they will.' },
        { kind: 'paragraph', text: 'The real question now is: Can you make this repeatable and scalable?' },
        { kind: 'paragraph', text: 'At this stage: growth is fragile; structure creates stability; discipline creates scale' },
        { kind: 'callout', text: 'Your evolution is: From selling → to building a revenue engine' },
      ]},
      { title: 'Your Revenue Evolution Path', blocks: [
        { kind: 'path', steps: ['Initial Revenue', 'Early Sales', 'Structured Growth', 'Repeatable Revenue', 'Scalable Business'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Revenue System & Growth Structuring' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'build your sales funnel',
          'define pricing structure',
          'identify winning channels',
          'implement KPI tracking',
        ]},
        { kind: 'paragraph', text: 'Outcome: From fragile growth → predictable revenue' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Document your full sales funnel',
          'Build a simple CRM (even Excel or Notion)',
          'Standardize your pricing',
          'Track CAC, LTV, and churn weekly',
          'Identify your top 1–2 acquisition channels',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Stabilize your sales before trying to scale them', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G3R4', group: 'G3', rCode: 'R4', domain: 'Revenue Readiness',
    stage: 'Structured Growth Stage (Optimization Phase)', tone: 'yellow', profile: 'Revenue Builder',
    intro: 'This report evaluates your ability to turn consistent revenue into a scalable, efficient, and financially disciplined growth system.',
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached a strong and important stage in your business: You are generating consistent revenue' },
        { kind: 'paragraph', text: 'This means: customers are paying regularly; revenue is recurring or predictable; acquisition channels are becoming clearer; your business is moving beyond experimentation' },
        { kind: 'paragraph', text: 'This is where startups begin to look like real companies.' },
        { kind: 'paragraph', text: 'However, consistency is not the same as scalability or profitability.' },
        { kind: 'paragraph', text: 'At this stage, your biggest challenge is: Turning growth into a financially disciplined and scalable system' },
        { kind: 'callout', text: 'You are currently in: The Growth Optimization Phase' },
      ]},
      { title: 'Revenue Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Revenue Stage' }, { text: 'Structured Growth', tone: 'yellow' }],
          [{ text: 'Revenue Status' }, { text: 'Consistent', tone: 'green' }],
          [{ text: 'Business Model' }, { text: 'Defined', tone: 'green' }],
          [{ text: 'Acquisition Channels' }, { text: 'Clear but not optimized', tone: 'yellow' }],
          [{ text: 'Pricing Strategy' }, { text: 'Structured', tone: 'yellow' }],
          [{ text: 'Metrics Discipline' }, { text: 'Developing', tone: 'orange' }],
          [{ text: 'Risk Level' }, { text: 'Medium', tone: 'yellow' }],
          [{ text: 'Probability of Success' }, { text: '55–70%' }],
        ]},
      ]},
      { title: 'Revenue Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Required for Scalable Growth' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required to Progress'], rows: [
          [{ text: 'Revenue' }, { text: 'Consistent', tone: 'green' }, { text: 'Must become scalable' }],
          [{ text: 'Channels' }, { text: 'Identified', tone: 'yellow' }, { text: 'Must be optimized' }],
          [{ text: 'Pricing' }, { text: 'Defined', tone: 'yellow' }, { text: 'Must be optimized' }],
          [{ text: 'Metrics' }, { text: 'Partial', tone: 'orange' }, { text: 'Full financial discipline' }],
          [{ text: 'Profitability' }, { text: 'Unclear', tone: 'orange' }, { text: 'Strong unit economics' }],
        ]},
        { kind: 'paragraph', text: 'You have a business, but not yet an optimized growth engine.' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Based on your diagnostic inputs:' },
        { kind: 'bullets', items: [
          'Consistent revenue is flowing (MRR or repeat customers)',
          'Pricing and packaging are mostly standardized',
          'Acquisition channels are known, but ROI varies',
          'CAC, LTV, and churn are tracked, but not systematically',
          'Growth feels structured, but not optimized',
        ]},
        { kind: 'callout', text: 'This creates: "Growth exists, but efficiency is unclear"' },
      ]},
      { title: 'Critical Risks Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Growth Without Profitability', sub: ['Revenue may be growing, but: margins may be weak; CAC may be too high; LTV may be unclear'] },
          { title: 'Weak Financial Discipline', sub: ['Without strong tracking: decisions become intuitive; scaling becomes risky'] },
          { title: 'Channel Inefficiency', sub: ['Channels may be working, but are: not optimized; not predictable enough'] },
          { title: 'Limited Monetization Optimization', sub: ['You may be: underpricing; not maximizing customer value; leaving revenue on the table'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 60–90 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Strengthen Growth Repeatability (CRITICAL)', sub: ['Focus on 1–2 best-performing channels; Eliminate low-ROI channels; Build playbooks for: acquisition; onboarding; customer success. Goal: Predictable and repeatable growth'] },
          { title: 'Professionalize Financial Tracking', sub: ["Move from spreadsheets to dashboards or tools. Track monthly: CAC vs LTV; burn rate; runway; revenue growth. Key principle: What you don't measure, you can't scale"] },
          { title: 'Refine Monetization Strategy', sub: ['Test pricing elasticity; Assess whether ARPU can be increased; Introduce: upsells; add-ons; Benchmark against competitors. Goal: Maximize revenue per customer'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are now in a serious business stage: You have traction + revenue' },
        { kind: 'paragraph', text: 'But now the game changes: Growth alone is not enough; Financial intelligence defines success' },
        { kind: 'paragraph', text: 'At this stage, investors ask: Can this scale profitably?; Are unit economics strong?; Is growth sustainable?' },
        { kind: 'callout', text: 'Your mission is: Turn revenue into a scalable and profitable system' },
      ]},
      { title: 'Your Revenue Evolution Path', blocks: [
        { kind: 'path', steps: ['Early Sales', 'Structured Growth', 'Optimized Growth', 'Scalable Revenue', 'Investor-Grade Business'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Growth Optimization & Financial Structuring' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'optimize acquisition channels',
          'improve unit economics',
          'structure financial tracking',
          'align growth with profitability',
        ]},
        { kind: 'paragraph', text: 'Outcome: From consistent revenue → scalable business' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Identify your top-performing acquisition channel',
          'Track CAC vs LTV monthly',
          'Improve conversion rates in your funnel',
          'Test pricing increases',
          'Monitor burn rate and runway',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Turn your revenue into a scalable and profitable growth engine', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G3R5', group: 'G3', rCode: 'R5', domain: 'Revenue Readiness',
    stage: 'Growth Acceleration Stage (Efficiency & Scale Phase)', tone: 'green', profile: 'Growth Driver',
    intro: 'This report evaluates your ability to scale revenue efficiently into an investor-grade growth engine.',
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached a high-impact stage in your business: Your revenue is growing consistently, and accelerating' },
        { kind: 'paragraph', text: 'This means: customers are adopting your product faster; acquisition channels are becoming predictable; revenue growth is visible month-over-month; your business model is validated' },
        { kind: 'paragraph', text: 'This is where startups transition into: real growth companies' },
        { kind: 'paragraph', text: 'However: Growth alone is not enough anymore' },
        { kind: 'paragraph', text: 'The key challenge now is: Scaling efficiently without breaking your business' },
        { kind: 'callout', text: 'You are currently in: The Growth Acceleration Phase' },
      ]},
      { title: 'Revenue Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Revenue Stage' }, { text: 'Growth Acceleration', tone: 'green' }],
          [{ text: 'Revenue Status' }, { text: 'Growing (MoM acceleration)', tone: 'green' }],
          [{ text: 'Acquisition Channels' }, { text: 'Repeatable', tone: 'green' }],
          [{ text: 'Unit Economics' }, { text: 'Healthy but not optimized', tone: 'yellow' }],
          [{ text: 'Financial Discipline' }, { text: 'Founder-led', tone: 'yellow' }],
          [{ text: 'Metrics Tracking' }, { text: 'Improving', tone: 'yellow' }],
          [{ text: 'Risk Level' }, { text: 'Medium', tone: 'yellow' }],
          [{ text: 'Probability of Success' }, { text: '70–85%' }],
        ]},
      ]},
      { title: 'Revenue Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Required for Investor-Grade Growth' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required to Progress'], rows: [
          [{ text: 'Revenue' }, { text: 'Growing fast', tone: 'green' }, { text: 'Must be efficient' }],
          [{ text: 'CAC' }, { text: 'Acceptable', tone: 'yellow' }, { text: 'Must decrease' }],
          [{ text: 'LTV' }, { text: 'Improving', tone: 'yellow' }, { text: 'Must increase' }],
          [{ text: 'Margins' }, { text: 'Unclear / variable', tone: 'orange' }, { text: 'Must be optimized' }],
          [{ text: 'Systems' }, { text: 'Semi-structured', tone: 'orange' }, { text: 'Fully system-driven' }],
          [{ text: 'Reporting' }, { text: 'Basic', tone: 'orange' }, { text: 'Investor-grade' }],
        ]},
        { kind: 'paragraph', text: 'You are scaling, but not yet optimized for efficiency' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'According to your report:' },
        { kind: 'bullets', items: [
          'Revenue growth is steady and accelerating',
          'CAC and LTV are tracked and improving',
          '1–2 acquisition channels are working consistently',
          'Financial discipline exists, but is still founder-led',
          'Churn is visible but not fully optimized',
        ]},
        { kind: 'callout', text: 'This creates: "Growth exists, but efficiency is not maximized"' },
      ]},
      { title: 'Critical Risks Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Growth Without Efficiency', sub: ['You may be growing, but: CAC may still be high; margins may be weak; profitability unclear'] },
          { title: 'Founder Dependency Risk', sub: ['Financial control depends on: founder decisions; not systems'] },
          { title: 'Infrastructure Limitation', sub: ['Tools and systems may not support scale'] },
          { title: 'Due Diligence Risk', sub: ['Investors will test: consistency; efficiency; financial discipline'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 60–90 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Optimize Unit Economics (CRITICAL)', sub: ['Reduce CAC: optimize best channels. Increase LTV: upsells; bundles; retention strategies. Improve margins: reduce COGS; improve operations. Goal: Efficient, scalable growth'] },
          { title: 'Strengthen Growth Infrastructure', sub: ['Move from basic CRM → structured tools. Automate: CAC tracking; LTV tracking; churn monitoring. Build playbooks for: onboarding; retention; growth. Goal: System-driven growth (not founder-driven)'] },
          { title: 'Prepare Investor-Grade Reporting', sub: ['Build monthly reporting: revenue; KPIs; unit economics. Highlight: MRR growth; retention cohorts; YoY growth. Goal: Speak investor language'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You have already achieved something significant: You proved your business can grow' },
        { kind: 'paragraph', text: 'But now the challenge is: "Can it grow efficiently and sustainably?"' },
        { kind: 'paragraph', text: 'At this stage: growth impresses; efficiency convinces' },
        { kind: 'paragraph', text: 'Investors now evaluate: CAC vs LTV; margins; scalability; financial discipline' },
        { kind: 'callout', text: 'Your evolution: From growth → to efficient scaling' },
      ]},
      { title: 'Your Revenue Evolution Path', blocks: [
        { kind: 'path', steps: ['Structured Growth', 'Growth Acceleration', 'Efficient Scaling', 'Investor-Grade Business', 'Market Leadership'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Growth Optimization & Investor Preparation' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'optimize unit economics',
          'structure financial reporting',
          'build scalable growth systems',
          'prepare for investor conversations',
        ]},
        { kind: 'paragraph', text: 'Outcome: From fast growth → efficient, investable growth' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Reduce CAC in your main channel',
          'Increase LTV through upsells',
          'Track margins weekly',
          'Build KPI dashboard',
          'Prepare monthly investor report',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Scale your growth, but make it efficient, predictable, and investor-ready', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G3R6', group: 'G3', rCode: 'R6', domain: 'Revenue Readiness',
    stage: 'Investor-Ready Stage (Financial Intelligence Phase)', tone: 'green', profile: 'Financially Disciplined',
    intro: 'This report evaluates your ability to operate your revenue with institutional-grade financial intelligence.',
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached a critical milestone in your business journey: You are now investor-ready from a financial perspective' },
        { kind: 'paragraph', text: 'This means: revenue is consistent and scaling; acquisition channels are repeatable; unit economics are measurable and improving; financial data is structured and defensible; reporting supports a credible growth story' },
        { kind: 'paragraph', text: 'You are no longer proving your business works. You are proving it is investable' },
        { kind: 'paragraph', text: 'You are currently operating in: The Financial Intelligence Phase' },
        { kind: 'paragraph', text: 'However: Being investor-ready is not the final stage' },
        { kind: 'callout', text: 'Your challenge now is: Turning discipline into institutional-grade performance' },
      ]},
      { title: 'Revenue Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Revenue Stage' }, { text: 'Investor-Ready', tone: 'green' }],
          [{ text: 'Revenue Status' }, { text: 'Consistent & Scaling', tone: 'green' }],
          [{ text: 'Acquisition Channels' }, { text: 'Repeatable', tone: 'green' }],
          [{ text: 'Unit Economics' }, { text: 'Strong & improving', tone: 'green' }],
          [{ text: 'Financial Data' }, { text: 'Accurate & defensible', tone: 'green' }],
          [{ text: 'Reporting' }, { text: 'Structured', tone: 'green' }],
          [{ text: 'Risk Level' }, { text: 'Medium–Low', tone: 'yellow' }],
          [{ text: 'Probability of Success' }, { text: '80–90%' }],
        ]},
      ]},
      { title: 'Revenue Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'You are investable, but not yet institutional' },
        { kind: 'table', headers: ['Area', 'Current Status', 'Required'], rows: [
          [{ text: 'Revenue' }, { text: 'Strong', tone: 'green' }, { text: 'Must scale further' }],
          [{ text: 'CAC / LTV' }, { text: 'Measured', tone: 'green' }, { text: 'Must optimize continuously' }],
          [{ text: 'Margins' }, { text: 'Acceptable', tone: 'yellow' }, { text: 'Must improve' }],
          [{ text: 'Reporting' }, { text: 'Structured', tone: 'green' }, { text: 'Must be institutional-grade' }],
          [{ text: 'Forecasting' }, { text: 'Basic', tone: 'orange' }, { text: 'Multi-scenario planning' }],
          [{ text: 'Decision-Making' }, { text: 'Semi-structured', tone: 'yellow' }, { text: 'Fully data-driven' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'bullets', items: [
          'Revenue is consistent and growing',
          'Acquisition is repeatable',
          'Financial metrics are tracked',
          'Reporting exists and supports decisions',
        ]},
        { kind: 'callout', text: 'This creates: "Growth + Discipline, but not yet institutionalized"' },
      ]},
      { title: 'Critical Risks', blocks: [
        { kind: 'steps', items: [
          { title: 'Weak forecasting capability' },
          { title: 'Narrative misalignment (story vs numbers)' },
          { title: 'Scaling without control' },
          { title: 'Complacency in execution' },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 60–90 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Institutionalize Financial Reporting (CRITICAL)', sub: ['Move from founder-led → structured FP&A. Automate KPI dashboards. Track: CAC, LTV, churn, margins. Goal: institutional-level credibility'] },
          { title: 'Strengthen Forecasting & Planning', sub: ['Build 3 scenarios: base / upside / downside. Align financials with hiring, GTM, runway. Goal: predictable and defendable growth'] },
          { title: 'Prepare for Investor Execution', sub: ['Integrate financials into pitch. Build investor snapshot (1-pager). Practice defending metrics. Goal: confidence in fundraising'] },
        ]},
      ]},
      { title: 'What This Means', blocks: [
        { kind: 'paragraph', text: 'You are no longer just growing. You are managing growth intelligently' },
        { kind: 'paragraph', text: 'At this stage: growth attracts attention; discipline builds trust' },
        { kind: 'paragraph', text: 'Investors now evaluate: clarity; consistency; credibility' },
        { kind: 'callout', text: 'Your evolution: Efficient Scaling → Financial Mastery' },
      ]},
      { title: 'Your Revenue Evolution Path', blocks: [
        { kind: 'path', steps: ['Growth Acceleration', 'Investor-Ready', 'Institutional Growth', 'Market Leadership'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Fundraising & Strategic Growth Package' },
        { kind: 'paragraph', text: 'Outcome: From investor-ready → funded and scaled' },
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: [
          'Automate KPI reporting',
          'Build multi-scenario financial model',
          'Create investor snapshot',
          'Align financials with pitch deck',
          'Prepare investor conversations',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'You are ready. Now position your business to raise capital successfully', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G3R7', group: 'G3', rCode: 'R7', domain: 'Revenue Readiness',
    stage: 'Market Leadership Stage (Leverage Phase)', tone: 'green', profile: 'Revenue Leader',
    intro: 'This report evaluates your ability to leverage market-leading revenue to dominate and attract capital.',
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached the highest level of revenue maturity: Your business is no longer just growing; It is dominating and attracting capital' },
        { kind: 'paragraph', text: 'This means: revenue is strong, predictable, and scalable; acquisition is optimized and multi-channel; unit economics are efficient and proven; growth is consistent and defensible; investor interest is high (or easily achievable)' },
        { kind: 'paragraph', text: 'You are no longer proving your business. You are controlling your leverage' },
        { kind: 'paragraph', text: 'You are now operating in: The Leverage & Market Leadership Phase' },
      ]},
      { title: 'Revenue Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Revenue Stage' }, { text: 'Market Leadership', tone: 'green' }],
          [{ text: 'Revenue Status' }, { text: 'Strong & Predictable', tone: 'green' }],
          [{ text: 'Acquisition Channels' }, { text: 'Scalable & Optimized', tone: 'green' }],
          [{ text: 'Unit Economics' }, { text: 'Optimized', tone: 'green' }],
          [{ text: 'Financial Discipline' }, { text: 'Institutional', tone: 'green' }],
          [{ text: 'Investor Demand' }, { text: 'High', tone: 'green' }],
          [{ text: 'Risk Level' }, { text: 'Low', tone: 'green' }],
          [{ text: 'Probability of Success' }, { text: '85–95%' }],
        ]},
      ]},
      { title: 'Revenue Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'No operational gaps, only strategic optimization' },
        { kind: 'table', headers: ['Area', 'Current Status', 'Required'], rows: [
          [{ text: 'Revenue' }, { text: 'Strong', tone: 'green' }, { text: 'Expand scale' }],
          [{ text: 'CAC / LTV' }, { text: 'Optimized', tone: 'green' }, { text: 'Maintain efficiency' }],
          [{ text: 'Margins' }, { text: 'Strong', tone: 'green' }, { text: 'Maximize profitability' }],
          [{ text: 'Channels' }, { text: 'Multi-channel', tone: 'green' }, { text: 'Expand dominance' }],
          [{ text: 'Reporting' }, { text: 'Institutional', tone: 'green' }, { text: 'Maintain precision' }],
          [{ text: 'Strategy' }, { text: 'Evolving', tone: 'yellow' }, { text: 'Optimize leverage' }],
        ]},
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'bullets', items: [
          'Revenue is predictable and scalable',
          'Growth is consistent across channels',
          'Unit economics are strong and optimized',
          'Financial systems are structured and reliable',
          'Business is attractive to investors',
        ]},
        { kind: 'callout', text: 'This creates: "Growth + Efficiency + Leverage"' },
      ]},
      { title: 'Strategic Risks', blocks: [
        { kind: 'steps', items: [
          { title: 'Wrong investor selection', sub: ['Choosing capital without strategic value'] },
          { title: 'Over-scaling risk', sub: ['Expanding too fast without control'] },
          { title: 'Strategic misalignment', sub: ['Growth without long-term direction'] },
          { title: 'Capital misuse', sub: ['Inefficient allocation reduces leverage'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 60–90 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Maximize Fundraising Leverage (CRITICAL)', sub: ['Run structured fundraising process; Create competition between investors; Control timeline and access. Goal: better terms + higher valuation'] },
          { title: 'Select Strategic Investors', sub: ['Prioritize: network; partnerships; market access. Avoid purely financial investors'] },
          { title: 'Expand Market Dominance', sub: ['Scale best-performing channels; Enter new segments / geographies; build partnerships. Goal: category leadership'] },
          { title: 'Optimize Capital Allocation', sub: ['Allocate capital to: growth; product; expansion. Goal: maximum ROI per dollar'] },
        ]},
      ]},
      { title: 'What This Means', blocks: [
        { kind: 'paragraph', text: 'You are in elite territory' },
        { kind: 'paragraph', text: 'At this stage: startups raise capital; you choose capital' },
        { kind: 'paragraph', text: "Investors don't evaluate you. They compete for you" },
        { kind: 'paragraph', text: 'Your advantage: Leverage' },
        { kind: 'paragraph', text: 'Your responsibility: Strategic decision-making' },
        { kind: 'callout', text: 'Your evolution: Investor-Ready → Market Leader' },
      ]},
      { title: 'Your Revenue Evolution Path', blocks: [
        { kind: 'path', steps: ['Investor-Ready', 'Market Leadership', 'Global Expansion', 'Category Dominance'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Strategic Growth & Capital Optimization' },
        { kind: 'paragraph', text: 'Outcome: From strong business → market leader' },
      ]},
      { title: 'Immediate Actions', blocks: [
        { kind: 'bullets', items: [
          'Build investor shortlist (tier 1 only)',
          'Structure fundraising timeline',
          'Define valuation targets',
          'Expand top-performing channels',
          'Align growth with long-term strategy',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'You have leverage. Use it to dominate your market and scale globally', tone: 'green' },
      ]},
    ],
  },
];
